import { Resource } from '../types';

/**
 * Gets the load time of the resource, if it exists
 * @param resourceEntries
 * @param resource Full/partial name or name regex pattern
 */
const getResourceLoadTime = (
  resourceEntries: PerformanceEntryList,
  resource: Resource,
): number | undefined => {
  const resourceEntry = resourceEntries.find(entry =>
    entry.name.match(resource),
  );

  // Doing some checks and casting because the TypeScript types don't seem to
  // be quite right for all cases here. Somewhat related issue:
  // https://github.com/microsoft/TypeScript/issues/33866
  if (resourceEntry && 'responseEnd' in resourceEntry) {
    const resourceAsTiming = resourceEntry as PerformanceResourceTiming;

    return resourceAsTiming.responseEnd;
  }

  return undefined;
};

/**
 * Returns the load time of the resource last to load
 * @param resources Collection of resource full/partial names & name regex patterns
 */
const getResourcesLoadTime = (
  resources: Resource[],
): Promise<number | null> => {
  return new Promise(resolve => {
    const getEntriesByTypeSupported =
      typeof window.performance.getEntriesByType === 'function';

    const check = () => {
      const resourceEntries = window.performance.getEntriesByType('resource');
      const resourceLoadTimes = resources.map(resource =>
        getResourceLoadTime(resourceEntries, resource),
      );

      const completedResourceLoadTimes: number[] = resourceLoadTimes.filter(
        (n): n is number => {
          return typeof n === 'number';
        },
      );
      const allResourcesLoaded =
        resourceLoadTimes.length === completedResourceLoadTimes.length;

      if (allResourcesLoaded) {
        // Since we wait until all resources have loaded before we return
        // the result, if a resource doesn't load (or there was a typo)
        // this will never return
        const latestResourceTime = Math.max(...completedResourceLoadTimes);

        resolve(latestResourceTime);
      } else {
        setTimeout(check, 250);
      }
    };

    if (
      getEntriesByTypeSupported &&
      resources instanceof Array &&
      resources.length > 0
    ) {
      check();
    } else {
      resolve(null);
    }
  });
};

export default getResourcesLoadTime;
